/**
 * Deriving a link's name, ensemble and subject area from the hubs it connects.
 *
 * A link created on the canvas is called `link_<timestamp>_l` until somebody renames it, and its
 * ensemble and subject area start empty. Now that hubs can be chosen in the blade, all three can
 * follow from that choice — the first hub chosen leads, matching every link name in production
 * (`customer__customer_group_l`, `avstamning__ersattningsprodukt__ersmo_produkter_l`).
 *
 * Measured on the production copy, over the 593 links whose first hub resolves by name:
 *   - the link's ensemble equals its first hub's in 567 (95%)
 *   - the link's subject area equals its first hub's in 563 (94%)
 *
 * The rest are deliberate — `customer__customer_payment_terms_l` takes the SECOND hub's ensemble,
 * and ST1's `sales_invoices__sales_invoice_lines_l` sits in `sa_vol` while its hub is in
 * `sa_finance`. So this is a DEFAULT, never an enforcement: anything the configurator typed
 * themselves is left alone (see `isDerivedValue`).
 */

/** The hub fields this needs. Keeps the rules testable without the workspace model. */
export type NamingHub = {
  targetTable?: string | null;
  ensemble?: string | null;
  subjectArea?: string | null;
};

export type DerivedLinkNaming = {
  targetTable: string;
  ensemble: string;
  subjectArea: string;
};

const HUB_SUFFIX = "_h";
const LINK_SUFFIX = "_l";

/** `customer_h` -> `customer`. Only a trailing `_h`, never one in the middle of a word. */
export const hubStem = (hubTargetTable: string | null | undefined): string => {
  const name = (hubTargetTable ?? "").trim();
  return name.toLowerCase().endsWith(HUB_SUFFIX) ? name.slice(0, -HUB_SUFFIX.length) : name;
};

/**
 * Build the naming a link would get from these hubs, in the order they were chosen.
 *
 * Returns empty strings when there is nothing to go on, so a caller can tell "no opinion" from
 * "call it this".
 */
export const deriveLinkNaming = (hubs: NamingHub[]): DerivedLinkNaming => {
  const stems = hubs.map((hub) => hubStem(hub.targetTable)).filter((stem) => stem.length > 0);
  if (stems.length === 0) {
    return { targetTable: "", ensemble: "", subjectArea: "" };
  }
  const first = hubs.find((hub) => hubStem(hub.targetTable).length > 0);
  return {
    targetTable: `${stems.join("__")}${LINK_SUFFIX}`,
    ensemble: (first?.ensemble ?? "").trim(),
    subjectArea: (first?.subjectArea ?? "").trim()
  };
};

/** A link the canvas named for itself, e.g. `link_1787664262836_l`. */
export const isPlaceholderLinkName = (value: string | null | undefined): boolean =>
  /^link_\d+_l$/i.test((value ?? "").trim());

/**
 * Is the value on screen still one this derived, rather than something a person typed?
 *
 * Re-deriving is only safe while nobody has taken ownership of the field. Rather than track an
 * "edited" flag — which does not survive a reload — compare against what the PREVIOUS hub
 * selection would have produced. Blank counts as underived, and so does the canvas placeholder,
 * because neither is a deliberate choice.
 */
export const isDerivedValue = (
  current: string | null | undefined,
  previousDerived: string,
  { allowPlaceholder = false }: { allowPlaceholder?: boolean } = {}
): boolean => {
  const value = (current ?? "").trim();
  if (value.length === 0) {
    return true;
  }
  if (allowPlaceholder && isPlaceholderLinkName(value)) {
    return true;
  }
  return value === previousDerived.trim();
};

/**
 * Work out which of a link's naming fields should follow a change of hubs.
 *
 * `previousHubs` is the selection BEFORE the change; a field still matching what those hubs
 * produced is treated as derived and moves on. Fields the configurator has taken over are absent
 * from the result, so the caller writes nothing for them.
 */
export const nextLinkNaming = (
  current: { targetTable?: string | null; ensemble?: string | null; subjectArea?: string | null },
  previousHubs: NamingHub[],
  nextHubs: NamingHub[]
): Partial<DerivedLinkNaming> => {
  const before = deriveLinkNaming(previousHubs);
  const after = deriveLinkNaming(nextHubs);
  const result: Partial<DerivedLinkNaming> = {};

  // A field is only included when it would actually CHANGE. Returning a value identical to what
  // is already on screen would mark the entity dirty for nothing, and an unexplained unsaved-change
  // badge is worse than no badge.
  const shouldSet = (
    derived: string,
    currentValue: string | null | undefined,
    previousDerived: string,
    allowPlaceholder = false
  ) =>
    Boolean(derived) &&
    derived !== (currentValue ?? "").trim() &&
    isDerivedValue(currentValue, previousDerived, { allowPlaceholder });

  if (shouldSet(after.targetTable, current.targetTable, before.targetTable, true)) {
    result.targetTable = after.targetTable;
  }
  if (shouldSet(after.ensemble, current.ensemble, before.ensemble)) {
    result.ensemble = after.ensemble;
  }
  if (shouldSet(after.subjectArea, current.subjectArea, before.subjectArea)) {
    result.subjectArea = after.subjectArea;
  }
  return result;
};
